/**
 * Hooks de la fonctionnalité « Reel Instagram d'un exercice » (admin).
 *
 * Réutilise le PIPELINE de rendu YouTube Studio (capture du showcase + compo
 * FFmpeg + worker) mais SANS playlist : on rend UN seul attempt de l'exercice en
 * vidéo verticale 9:16, puis on publie en Instagram Reels.
 *
 * Le rendu est une ligne `youtube_videos` (spec agnostique plateforme) insérée
 * directement (RLS admin) — sans `youtube_video_items` (le worker retombe sur
 * l'item primaire `exercise_id`/`attempt_id`). L'enqueue passe par l'edge fn
 * `youtube-enqueue` avec `platforms=['instagram_reels']`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { VideoOverlay } from '@jeraisonne/db/schemas/youtube';
import { supabase } from '@/lib/supabase';
import { ytDb, type YoutubeVideoRow } from '@/lib/admin/youtube-queries';

/** Réponse complétée d'un exercice, candidate au rendu Reel. */
export interface ExerciseAttemptOption {
  attempt_id: string;
  student_name: string;
  completed_at: string | null;
}

/** Attempts `completed` de CET exercice (toutes réponses élèves), récentes d'abord. */
export function useExerciseCompletedAttempts(exerciseId: string) {
  return useQuery({
    queryKey: ['exercise-reel-attempts', exerciseId],
    enabled: !!exerciseId,
    queryFn: async (): Promise<ExerciseAttemptOption[]> => {
      const { data, error } = await supabase
        .from('attempts')
        .select('id, completed_at, profiles!attempts_student_id_fkey ( display_name:visible_name )')
        .eq('exercise_id', exerciseId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []).map((a: any) => ({
        attempt_id: a.id,
        student_name: a.profiles?.display_name ?? 'Élève',
        completed_at: a.completed_at,
      }));
    },
  });
}

/** Plateformes de diffusion d'un reel d'exercice (verticales courtes). */
export type ExerciseReelPlatform = 'instagram_reels' | 'tiktok';

/** Invoque `youtube-enqueue` (rendu/publication Reel) avec erreur lisible. */
async function enqueueReel(
  videoId: string,
  platforms: ExerciseReelPlatform[],
  mode: 'preview' | 'publish',
): Promise<void> {
  const { error } = await supabase.functions.invoke('youtube-enqueue', {
    body: { video_id: videoId, platforms, privacy: 'public', mode },
  });
  if (error) {
    let message = error.message;
    try {
      const ctxBody = await (
        error as { context?: { json?: () => Promise<unknown> } }
      ).context?.json?.();
      const obj = ctxBody as { error?: string; message?: string } | undefined;
      message = obj?.message ?? obj?.error ?? message;
    } catch {
      /* garde le message générique */
    }
    throw new Error(message);
  }
}

export interface CreateExerciseReelInput {
  exerciseId: string;
  attemptId: string;
  userId: string;
  overlay: VideoOverlay;
  caption: string;
  /** Asset musique (youtube_assets, kind='music'). null = Reel muet. */
  musicAssetId?: string | null;
  /** Palier de durée (s). Défaut 30 (Reel court). */
  durationSeconds?: number;
  /** Plateformes ciblées (≥1). Défaut Instagram Reels. */
  platforms: ExerciseReelPlatform[];
}

/**
 * Crée le rendu d'un Reel (1 attempt, portrait, fond uni) et l'enfile pour
 * Instagram. Retourne l'id `youtube_videos` créé (pour suivre le statut).
 */
export function useCreateExerciseReel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateExerciseReelInput): Promise<string> => {
      const row = {
        created_by: input.userId,
        exercise_id: input.exerciseId,
        attempt_id: input.attemptId,
        background_asset_id: null,
        music_asset_id: input.musicAssetId ?? null,
        aspect_ratio: 'portrait',
        duration_seconds: input.durationSeconds ?? 30,
        loop_background: true,
        overlay: input.overlay,
        yt_title: null,
        yt_description: null,
        yt_tags: [],
        social_caption: input.caption || null,
      };
      const { data, error } = await ytDb
        .from('youtube_videos')
        .insert(row)
        .select('id')
        .maybeSingle();
      if (error) throw new Error(error.message);
      const video = data as Pick<YoutubeVideoRow, 'id'>;

      // Validation manuelle : on RREND seulement (aperçu). La publication se fera
      // après le clic « Publier » (usePublishExerciseReel).
      await enqueueReel(video.id, input.platforms, 'preview');
      return video.id;
    },
    onSuccess: (videoId) => {
      qc.invalidateQueries({ queryKey: ['youtube-video', videoId] });
      qc.invalidateQueries({ queryKey: ['youtube-videos'] });
    },
  });
}

/** Approuve l'aperçu → publie le Reel sur les plateformes choisies (master déjà rendu). */
export function usePublishExerciseReel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      videoId: string;
      platforms: ExerciseReelPlatform[];
    }): Promise<string> => {
      await enqueueReel(input.videoId, input.platforms, 'publish');
      return input.videoId;
    },
    onSuccess: (videoId) => {
      qc.invalidateQueries({ queryKey: ['youtube-video', videoId] });
    },
  });
}

/**
 * URL signée du master.mp4 rendu (aperçu de validation). `null` tant que le rendu
 * n'est pas terminé → on re-tente toutes les 5 s jusqu'à l'obtenir.
 */
export function useReelPreviewUrl(videoId: string | null) {
  return useQuery({
    queryKey: ['reel-preview-url', videoId],
    enabled: !!videoId,
    refetchInterval: (query) => (query.state.data ? false : 5000),
    queryFn: async (): Promise<string | null> => {
      if (!videoId) return null;
      const { data } = await supabase.storage
        .from('youtube-renders')
        .createSignedUrl(`${videoId}/master.mp4`, 3600);
      return data?.signedUrl ?? null;
    },
  });
}
